import { useCallback, useMemo, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import type { WatchComment } from "@/hooks/use-watch";

// ── Types ────────────────────────────────────────────────────────────────────

export type TimelineComment = {
  id: string;
  timestampSeconds: number;   // pre-filtered — null timestamps skipped upstream
  authorName: string;
  sequence: number;           // #N in createdAt-asc order
  hasAnchor: boolean;         // shows a pin marker instead of a dot
};

type Props = {
  currentTime: number;
  duration: number;
  comments: WatchComment[];
  onSeek: (seconds: number) => void;
  /** Toggle from Settings → View on Asset → Anchored Comments (default true). */
  showAnchorMarkers?: boolean;
  /** Id of the comment currently selected in the right panel — dot renders larger. */
  selectedCommentId?: string | null;
  /** Marker click reports the comment id so the right panel can select + scroll it. */
  onSelectComment?: (id: string) => void;
};

// ── Component ────────────────────────────────────────────────────────────────

/**
 * Timeline scrubber (Task 8).
 *
 * Sits directly above the bottom controls bar. Shows:
 *   - Playback progress bar (filled up to currentTime / duration)
 *   - Draggable playhead knob
 *   - One dot per commented timecode. Anchor comments render a pin instead.
 *   - Avatar chip above each dot
 *   - Hover on dot → tooltip: "Author — HH:MM:SS.MS — #N"
 *   - Click dot → seek to that timecode
 *   - Click/drag anywhere on the bar → seek
 *
 * Sequence numbers (#N) are computed here from the ordered comment list — no
 * backend field. `WatchComment.createdAt` drives the ordering.
 */
export function TimelineScrubber({
  currentTime,
  duration,
  comments,
  onSeek,
  showAnchorMarkers = true,
  selectedCommentId = null,
  onSelectComment,
}: Props) {
  const barRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Filter + number. `sequence` is 1-based, ordered by createdAt ASC.
  const markers: TimelineComment[] = useMemo(() => {
    const sorted = [...comments].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
    return sorted
      .map((c, i) => ({
        id: c.id,
        timestampSeconds: c.timestampSeconds ?? -1,
        authorName: c.author?.name || (c.author?.type === "guest" ? "Guest" : "Someone"),
        sequence: i + 1,
        hasAnchor: c.positionX != null && c.positionY != null,
      }))
      .filter((m) => m.timestampSeconds >= 0);
  }, [comments]);

  const progress = duration > 0 ? Math.min(1, Math.max(0, currentTime / duration)) : 0;

  const seekAtEvent = useCallback(
    (clientX: number) => {
      if (!barRef.current || duration <= 0) return;
      const rect = barRef.current.getBoundingClientRect();
      const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      onSeek(frac * duration);
    },
    [duration, onSeek],
  );

  // Pointer drag support — captures pointer so drag continues even if the
  // cursor leaves the bar's bounds. Works with mouse + touch + pen.
  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Don't seek when the click bubbled up from a marker (marker handles its own click).
      if ((e.target as HTMLElement).closest("[data-timeline-marker]")) return;
      setIsDragging(true);
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      seekAtEvent(e.clientX);
    },
    [seekAtEvent],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDragging) return;
      seekAtEvent(e.clientX);
    },
    [isDragging, seekAtEvent],
  );

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    setIsDragging(false);
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* pointer may not have been captured (marker click path) */
    }
  }, []);

  return (
    <div
      className="relative select-none px-4 pb-2 pt-3"
      // Extra room above the bar so avatar chips can sit visually above it
      // without pushing controls further down.
    >
      {/* Track */}
      <div
        ref={barRef}
        className="relative h-1 w-full cursor-pointer rounded-full bg-black/10 dark:bg-white/15"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        {/* Progress fill */}
        <div
          className="pointer-events-none absolute inset-y-0 left-0 rounded-full bg-primary"
          style={{ width: `${progress * 100}%` }}
        />
        {/* Playhead */}
        <div
          className="pointer-events-none absolute top-1/2 h-3 w-3 -translate-y-1/2 rounded-full border-2 border-white bg-primary shadow"
          style={{ left: `calc(${progress * 100}% - 6px)` }}
        />

        {/* Comment markers */}
        {markers.map((m) => (
          <TimelineMarker
            key={m.id}
            marker={m}
            duration={duration}
            showAnchorMarkers={showAnchorMarkers}
            selected={selectedCommentId === m.id}
            onSeek={onSeek}
            onSelect={onSelectComment}
          />
        ))}
      </div>
    </div>
  );
}

// ── Marker ──────────────────────────────────────────────────────────────────

function TimelineMarker({
  marker,
  duration,
  showAnchorMarkers,
  selected,
  onSeek,
  onSelect,
}: {
  marker: TimelineComment;
  duration: number;
  showAnchorMarkers: boolean;
  selected: boolean;
  onSeek: (seconds: number) => void;
  onSelect?: (id: string) => void;
}) {
  const [hovered, setHovered] = useState(false);

  if (duration <= 0) return null;
  const left = (marker.timestampSeconds / duration) * 100;

  const isPin = marker.hasAnchor && showAnchorMarkers;

  return (
    <div
      data-timeline-marker
      className={cn(
        "absolute top-1/2 z-10 -translate-x-1/2 -translate-y-1/2",
        selected && "z-20",
      )}
      style={{ left: `${left}%` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={(e) => {
        e.stopPropagation();
        onSeek(marker.timestampSeconds);
        onSelect?.(marker.id);
      }}
    >
      {/* Avatar chip above the marker — quick author ID at a glance. */}
      <div
        className="pointer-events-none absolute left-1/2 grid h-5 w-5 -translate-x-1/2 -translate-y-full place-items-center rounded-full border border-black/20 bg-white text-[10px] font-semibold uppercase text-foreground dark:border-white/20 dark:bg-neutral-800 dark:text-white"
        style={{ top: -6 }}
      >
        {authorInitials(marker.authorName)}
      </div>

      {/* Dot or pin — pin for anchor comments when the toggle is on. Selected
          markers scale up + get a ring so the timeline mirrors the right
          panel's selection state. */}
      {isPin ? (
        <MapPin
          className={cn(
            "cursor-pointer text-primary transition-transform",
            selected ? "h-4 w-4 scale-125 drop-shadow" : "h-3 w-3",
          )}
          fill="currentColor"
          strokeWidth={0}
        />
      ) : (
        <div
          className={cn(
            "cursor-pointer rounded-full border-2 border-white bg-primary shadow transition-transform",
            selected ? "h-3.5 w-3.5 scale-110 ring-2 ring-primary/50" : "h-2.5 w-2.5",
          )}
        />
      )}

      {/* Hover tooltip: author + timecode + sequence #N */}
      {hovered && (
        <div
          className={cn(
            "pointer-events-none absolute left-1/2 z-20 -translate-x-1/2",
            "whitespace-nowrap rounded-md border border-black/10 bg-white px-2 py-1 text-xs text-foreground shadow-lg dark:border-white/10 dark:bg-neutral-900 dark:text-white",
          )}
          style={{ bottom: 32 }}
        >
          <div className="font-medium">{marker.authorName}</div>
          <div className="text-[11px] text-black/60 dark:text-white/60">
            {formatTimecode(marker.timestampSeconds)} · #{marker.sequence}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────
function formatTimecode(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const totalMs = Math.floor(seconds * 1000);
  const hh = Math.floor(totalMs / 3_600_000);
  const mm = Math.floor((totalMs % 3_600_000) / 60_000);
  const ss = Math.floor((totalMs % 60_000) / 1000);
  const ms = totalMs % 1000;
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}.${padMs(ms)}`;
}
const pad = (n: number) => n.toString().padStart(2, "0");
const padMs = (n: number) => n.toString().padStart(3, "0");

function authorInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
